import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import {
  Search, Command, ArrowRight, Clock, Briefcase, Building2, Users,
  TrendingUp, FileText, DollarSign, BarChart3, Bookmark, Activity,
  ChevronRight, Zap
} from "lucide-react";

import { usePageNavigate } from "../../hooks/usePageNavigate";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PAGES = [
  { id: "home", label: "Home", icon: Command, category: "Pages" },
  { id: "market", label: "Loan Market", icon: BarChart3, category: "Pages" },
  { id: "investor-marketplace", label: "Investment Opportunities", icon: TrendingUp, category: "Pages" },
  { id: "secondary-market", label: "Secondary Market", icon: DollarSign, category: "Pages" },
  { id: "activity-feed", label: "Activity Feed", icon: Activity, category: "Pages" },
  { id: "watchlist", label: "My Watchlist", icon: Bookmark, category: "Pages" },
  { id: "insights", label: "Insights", icon: FileText, category: "Pages" },
  { id: "contact", label: "Contact", icon: FileText, category: "Pages" },
  { id: "profile", label: "My Profile", icon: Users, category: "Pages" },
];

const ENTITY_ICONS = { deal: Briefcase, borrower: Users, lender: Building2, investor: TrendingUp, opportunity: DollarSign, document: FileText, commitment: DollarSign };
const ENTITY_COLORS = { deal: "text-blue-600", borrower: "text-amber-600", lender: "text-purple-600", investor: "text-emerald-600", opportunity: "text-gold-dark", document: "text-slate-600" };

const RECENT_KEY = "ic_recent_entities";

export function getRecentEntities() {
  try { return JSON.parse(localStorage.getItem(RECENT_KEY) || "[]"); } catch { return []; }
}

export function trackEntityView(type, id, title) {
  const recent = getRecentEntities().filter(e => !(e.type === type && e.id === id));
  recent.unshift({ type, id, title, viewedAt: new Date().toISOString() });
  localStorage.setItem(RECENT_KEY, JSON.stringify(recent.slice(0, 20)));
}

export default function JumpBar({ isOpen, onClose }) {
  const { navigateTo, navigateToEntity } = usePageNavigate();
  const [query, setQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selectedIdx, setSelectedIdx] = useState(0);
  const inputRef = useRef(null);
  const token = localStorage.getItem("ic_token");

  useEffect(() => {
    if (isOpen) {
      setQuery("");
      setSearchResults([]);
      setSelectedIdx(0);
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  const recentEntities = useMemo(() => getRecentEntities().slice(0, 6), [isOpen]);

  const filteredPages = useMemo(() => {
    if (!query) return [];
    const q = query.toLowerCase();
    return PAGES.filter(p => p.label.toLowerCase().includes(q));
  }, [query]);

  const searchEntities = useCallback(async (q) => {
    if (!q || q.length < 2 || !token) return;
    setSearching(true);
    try {
      const res = await axios.get(`${API}/search/global?q=${encodeURIComponent(q)}`, { headers: { Authorization: `Bearer ${token}` } });
      setSearchResults(res.data.results || []);
    } catch { setSearchResults([]); }
    finally { setSearching(false); }
  }, [token]);

  useEffect(() => {
    const timer = setTimeout(() => searchEntities(query), 250);
    return () => clearTimeout(timer);
  }, [query, searchEntities]);

  const allItems = useMemo(() => {
    const items = [];
    if (!query && recentEntities.length > 0) {
      items.push({ type: "header", label: "Recent" });
      recentEntities.forEach(e => items.push({ type: "recent", ...e, category: "Recent" }));
    }
    if (filteredPages.length > 0) {
      items.push({ type: "header", label: "Pages" });
      filteredPages.forEach(p => items.push({ type: "page", ...p, category: "Pages" }));
    }
    if (searchResults.length > 0) {
      items.push({ type: "header", label: "Entities" });
      searchResults.forEach(r => items.push({ type: "entity", ...r, category: "Entities" }));
    }
    if (!query && items.length === 0) {
      items.push({ type: "header", label: "Quick Navigation" });
      PAGES.slice(0, 6).forEach(p => items.push({ type: "page", ...p }));
    }
    return items;
  }, [query, filteredPages, searchResults, recentEntities]);

  const selectableItems = allItems.filter(i => i.type !== "header");

  const handleSelect = (item) => {
    if (item.type === "page") {
      navigateTo(item.id);
    } else if (item.type === "entity") {
      const entityType = item.type === "entity" ? (item.entityType || "deal") : item.type;
      navigateToEntity(entityType, item.id);
    } else if (item.type === "recent") {
      navigateToEntity(item.entityType || item.type || "deal", item.id);
    }
    onClose();
  };

  const handleKeyDown = (e) => {
    if (e.key === "ArrowDown") { e.preventDefault(); setSelectedIdx(i => Math.min(i + 1, selectableItems.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setSelectedIdx(i => Math.max(i - 1, 0)); }
    else if (e.key === "Enter" && selectableItems[selectedIdx]) { handleSelect(selectableItems[selectedIdx]); }
    else if (e.key === "Escape") { onClose(); }
  };

  useEffect(() => { setSelectedIdx(0); }, [query]);

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div className="fixed inset-0 z-[60] flex items-start justify-center pt-[15vh]" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
        <motion.div className="relative w-full max-w-xl bg-white rounded-2xl shadow-2xl border border-[#e8e0d0] overflow-hidden"
          initial={{ opacity: 0, y: -20, scale: 0.95 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: -20 }} data-testid="jump-bar">
          {/* Search Input */}
          <div className="flex items-center gap-3 px-4 py-3.5 border-b border-[#e8e0d0]">
            <Search className="h-5 w-5 text-[#999] shrink-0" />
            <input ref={inputRef} value={query} onChange={e => setQuery(e.target.value)} onKeyDown={handleKeyDown}
              placeholder="Jump to page, entity, or deal..."
              className="flex-1 bg-transparent text-sm text-[#222] outline-none placeholder:text-[#bbb]"
              data-testid="jump-bar-input" />
            <kbd className="hidden sm:flex items-center gap-0.5 px-1.5 py-0.5 rounded border border-[#ddd] text-[10px] text-[#999] bg-[#faf7f2]">ESC</kbd>
          </div>

          {/* Results */}
          <div className="max-h-[50vh] overflow-y-auto py-2" data-testid="jump-bar-results">
            {searching && (
              <div className="px-4 py-3 text-xs text-[#999] flex items-center gap-2">
                <span className="h-3 w-3 border-2 border-emerald-600 border-t-transparent rounded-full animate-spin" /> Searching...
              </div>
            )}
            {allItems.map((item, i) => {
              if (item.type === "header") {
                return <div key={`h-${item.label}`} className="px-4 pt-3 pb-1 text-[9px] font-bold uppercase tracking-wider text-[#bbb]">{item.label}</div>;
              }
              const selectIdx = selectableItems.indexOf(item);
              const isSelected = selectIdx === selectedIdx;
              const Icon = item.type === "page" ? (item.icon || Command) : (ENTITY_ICONS[item.type === "recent" ? item.type : (item.type || "deal")] || Briefcase);
              const color = item.type === "page" ? "text-emerald-600" : (ENTITY_COLORS[item.type === "recent" ? item.type : item.type] || "text-[#888]");

              return (
                <button key={`${item.type}-${item.id}-${i}`} onClick={() => handleSelect(item)}
                  onMouseEnter={() => setSelectedIdx(selectIdx)}
                  className={`w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors ${isSelected ? "bg-emerald-50" : "hover:bg-[#faf7f2]"}`}
                  data-testid={`jump-result-${item.id || i}`}>
                  <div className={`h-7 w-7 rounded-lg flex items-center justify-center shrink-0 ${isSelected ? "bg-emerald-100" : "bg-[#f5f5f5]"}`}>
                    <Icon className={`h-3.5 w-3.5 ${color}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-[#222] truncate">{item.label || item.title || item.id}</div>
                    {item.subtitle && <div className="text-[10px] text-[#999] truncate">{item.subtitle}</div>}
                    {item.type === "recent" && item.viewedAt && (
                      <div className="text-[10px] text-[#bbb] flex items-center gap-1"><Clock className="h-2.5 w-2.5" />{new Date(item.viewedAt).toLocaleString()}</div>
                    )}
                  </div>
                  <ChevronRight className={`h-3.5 w-3.5 shrink-0 ${isSelected ? "text-emerald-600" : "text-[#ddd]"}`} />
                </button>
              );
            })}
            {!searching && query && selectableItems.length === 0 && (
              <div className="px-4 py-8 text-center text-sm text-[#999]">No results for "{query}"</div>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between px-4 py-2 border-t border-[#e8e0d0] bg-[#faf7f2]">
            <div className="flex items-center gap-3 text-[10px] text-[#bbb]">
              <span className="flex items-center gap-1"><kbd className="px-1 py-0.5 rounded border border-[#ddd] bg-white text-[9px]">↑↓</kbd> Navigate</span>
              <span className="flex items-center gap-1"><kbd className="px-1 py-0.5 rounded border border-[#ddd] bg-white text-[9px]">↵</kbd> Select</span>
              <span className="flex items-center gap-1"><kbd className="px-1 py-0.5 rounded border border-[#ddd] bg-white text-[9px]">esc</kbd> Close</span>
            </div>
            <div className="flex items-center gap-1 text-[10px] text-emerald-600">
              <Zap className="h-3 w-3" /> Capital OS
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
